import type { ActionState, QaLevel, QaLoop, RunStatus } from "./types";

/**
 * THE HONEST QA INSTRUMENT.
 *
 * This file replaces two functions that lied to the operator:
 *
 *   qaProgressPercent()  returned a hardcoded 72 while running and floored the result at
 *                        42 / 35 regardless of what had actually passed, so the UI printed
 *                        "42% · 1/6 checks passed" (1/6 is 17%) underneath the words
 *                        "Final QA can run".
 *   fallbackQaLevels()   manufactured a "pass" verdict for gates that never executed:
 *                        `if (completed) return "pass"` inferred a pass from the run being
 *                        approved, and `if (qaResultAvailable) return "pass"` inferred one
 *                        from a qaLoop OBJECT merely existing.
 *
 * Both are DELETED. There is no percentage here to fabricate and no branch that can invent a
 * verdict, because the type system will not allow one:
 *
 *   A Gate may only carry state "passed" if it also carries `evidence: QaLevel` — the actual
 *   backend level whose status was "pass". `{ state: "passed", evidence: null }` DOES NOT
 *   COMPILE. That is the load-bearing guarantee of this product: there exists no code path,
 *   none, by which a green tick can be drawn for a check that did not run.
 *
 * A progress bar is also the wrong instrument in principle. It implies monotonic advance,
 * but a QA gate can FAIL, and "failed" is not "less progress". The UI shows counts.
 */

/** The six words. Nothing else may appear on any screen. `not_run` is the DEFAULT. */
export type GateState = "not_run" | "running" | "passed" | "failed" | "warned" | "blocked" | "not_applicable";

export type GateTone = "neutral" | "progress" | "success" | "caution" | "danger";

export const GATE_STATE_META = {
  not_run: { label: "Not run", tone: "neutral" },
  running: { label: "Checking", tone: "progress" },
  passed: { label: "Passed", tone: "success" },
  failed: { label: "Failed", tone: "danger" },
  blocked: { label: "Blocked", tone: "danger" },
  // A WARNING IS NOT A FAILURE, AND IT IS NOT A PASS.
  //
  // This state used to be rendered as `blocked`, with the words "Export requires a clean pass." That was
  // TRUE of the old backend — `aligned` required warningCount === 0 — and it made the whole `warn`
  // severity a lie: V8b, which reports that an edit made a claim MORE tentative (a safe thing), blocked
  // the export exactly as hard as a protected fact going missing. A product that can only shout gets
  // ignored, and an operator trained to click through warnings clicks through the real blocker too.
  //
  // The backend no longer blocks on warnings (only hardFailureCount and level.status === "fail" block),
  // so this row now says what it means: look at this; it is not stopping you.
  warned: { label: "Warning", tone: "caution" },
  not_applicable: { label: "Not applicable", tone: "neutral" }
} satisfies Record<GateState, { label: string; tone: GateTone }>;

/**
 * The SIX required QA levels. These are not invented here: they are the backend's own
 * `qaLoopPolicy.requiredLevelIds`, in order. The old frontend list was wrong in two ways —
 * it omitted the real "editable-blocks" gate, and it added a synthetic "Edit decisions" row
 * that is a review-stage PRECONDITION, not a QA check. Counting that precondition in the
 * denominator was the sole source of the phantom "1/6 passed" shown while QA had
 * demonstrably not run. The precondition now lives where it belongs: in the gate that
 * enables the Run button.
 */
export const GATE_DEFS = [
  { id: "protected-facts", label: "Protected facts" },
  { id: "editable-blocks", label: "Editable blocks" },
  { id: "research-rigor", label: "Research rigor" },
  { id: "patch-application", label: "Patch application" },
  { id: "rendering", label: "Rendering and responsive QA" },
  { id: "reviewers", label: "Reviewer sign-off" }
] as const;

export type GateId = (typeof GATE_DEFS)[number]["id"];

type GateBase = {
  id: string;
  label: string;
  /** The blocking reason renders INSIDE the row that is blocking. Never exiled to a card. */
  messages: string[];
};

/**
 * THE TYPE THAT MAKES DISHONESTY UNCOMPILABLE.
 *
 * "passed" and "failed" REQUIRE the backend QaLevel that produced them. Everything else
 * carries `evidence: null` — and cannot be "passed".
 */
export type Gate =
  | (GateBase & { state: "passed"; evidence: QaLevel })
  | (GateBase & { state: "failed"; evidence: QaLevel })
  | (GateBase & { state: "warned"; evidence: QaLevel })
  | (GateBase & { state: "blocked"; evidence: QaLevel | null })
  | (GateBase & { state: "not_run" | "running" | "not_applicable"; evidence: null });

export function qaAttemptCount(qaLoop?: QaLoop) {
  if (!qaLoop?.attempts) return 0;
  return Array.isArray(qaLoop.attempts) ? qaLoop.attempts.length : Number(qaLoop.attempts) || 0;
}

/**
 * EVERY issue on the level, never a truncated three.
 *
 * This used to end in `.slice(0, 3)`. A real V5 on the Gemini grounding-marker leak emits ~24
 * issues across 24 places; the operator would have been shown three and told the other 21 did not
 * exist. A governance instrument may not hide the scale of a finding. The count of what is rendered
 * therefore EQUALS the count of what the backend found — see issueCount().
 */
export function qaLevelMessages(level: QaLevel) {
  return (level.issues || [])
    .map((issue) => issue.message || issue.id || issue.validator || "")
    .filter(Boolean);
}

/** How many issues the backend reported on this level. The denominator of "N of M shown". */
export function issueCount(level: QaLevel) {
  return (level.issues || []).length;
}

export type ChangeBlocker = { gateId: string; gateLabel: string; message: string };

/**
 * CROSS-LINK A BLOCKER TO THE EDIT IT IMPLICATES — BY EXACT BLOCK ID, AND NOTHING ELSE.
 *
 * This replaces a page/title case-insensitive SUBSTRING match. With register-driven messages like
 * "Protected fact pf-014 altered", a substring of the page ("Page 2") or the title ("Opening frame")
 * appears in NO such message, so the substring path silently attached the blocker to nothing and a
 * blocked run again presented calm green cards. The match is now `issue.blockId === change.blockId`,
 * exact — the same id space the backend patcher and the block register use. If a change carries no
 * blockId (local preview) or nothing matches, nothing extra renders and the blocker stays in its
 * gate row. Mis-attributing a blocker on a governance screen is worse than not linking at all.
 */
export function blockersForChange(gates: Gate[], change: { blockId?: string }): ChangeBlocker[] {
  const blockId = change.blockId?.trim();
  if (!blockId) return [];
  const out: ChangeBlocker[] = [];
  for (const gate of gates) {
    if (gate.state !== "blocked" && gate.state !== "failed") continue;
    for (const issue of gate.evidence?.issues || []) {
      if (issue.blockId !== blockId) continue;
      const message = issue.message || issue.id || issue.validator || `${gate.label} flagged this block.`;
      out.push({ gateId: gate.id, gateLabel: gate.label, message });
    }
  }
  return out;
}

/**
 * A run is QA-VERIFIED only when the backend says every required level aligned.
 * Not when the run is "approved". Not when a qaLoop object happens to exist.
 */
export function qaVerified(run: RunStatus) {
  return run.qaLoop?.aligned === true;
}

/**
 * THE ADVISORY MODEL.
 *
 * In the Studio, QA advises — it does not block — with exactly ONE exception: a protected-fact
 * violation. Every finding on any gate EXCEPT `protected-facts` is an overridable advisory; the
 * `protected-facts` gate is the single hard stop, surfaced as its own object and the only thing
 * that disables export. This is the operator's decision: deterministic protected-fact failures
 * still stop the export cold, but a layout, research, or reviewer finding becomes advice.
 */
export type Advisory = {
  gateId: string;
  gateLabel: string;
  message: string;
  validator?: string;
  blockId?: string;
};

const HARD_STOP_GATE = "protected-facts";

function issueText(issue: { message?: string; id?: string; validator?: string }, fallback: string) {
  return issue.message || issue.id || issue.validator || fallback;
}

/** Non-blocking findings for ONE block — everything flagged on it except a protected-fact violation. */
export function advisoriesForBlock(gates: Gate[], blockId: string): Advisory[] {
  const id = blockId?.trim();
  if (!id) return [];
  const out: Advisory[] = [];
  for (const gate of gates) {
    if (gate.id === HARD_STOP_GATE) continue;
    for (const issue of gate.evidence?.issues || []) {
      if (issue.blockId !== id) continue;
      out.push({ gateId: gate.id, gateLabel: gate.label, message: issueText(issue, gate.label), validator: issue.validator, blockId: id });
    }
  }
  return out;
}

/** Run-level findings (no blockId) from every gate except protected-facts — they go to the run tray. */
export function runLevelAdvisories(gates: Gate[]): Advisory[] {
  const out: Advisory[] = [];
  for (const gate of gates) {
    if (gate.id === HARD_STOP_GATE) continue;
    for (const issue of gate.evidence?.issues || []) {
      if (issue.blockId) continue;
      out.push({ gateId: gate.id, gateLabel: gate.label, message: issueText(issue, gate.label), validator: issue.validator });
    }
  }
  return out;
}

/** The one hard stop: protected-fact violations. A non-empty result means export is blocked. */
export function protectedFactViolations(gates: Gate[]): Advisory[] {
  const gate = gates.find((item) => item.id === HARD_STOP_GATE);
  if (!gate || (gate.state !== "failed" && gate.state !== "blocked")) return [];
  const out: Advisory[] = (gate.evidence?.issues || []).map((issue) => ({
    gateId: gate.id,
    gateLabel: gate.label,
    message: issueText(issue, "A protected fact does not match the canonical source."),
    validator: issue.validator,
    blockId: issue.blockId
  }));
  if (!out.length) out.push({ gateId: gate.id, gateLabel: gate.label, message: gate.messages[0] || "A protected fact does not match the canonical source." });
  return out;
}

/**
 * Protected facts are clear — export is permitted. True when the gate passed, or when QA has not
 * run yet (nothing has been violated). False only when the gate actually failed/blocked. This is
 * the ONLY thing the Studio lets stop an export.
 */
export function protectedFactsClear(gates: Gate[]): boolean {
  const gate = gates.find((item) => item.id === HARD_STOP_GATE);
  if (!gate) return true;
  return gate.state !== "failed" && gate.state !== "blocked";
}

/** The backend reported a QA result and it did NOT align. */
export function qaRejected(run: RunStatus) {
  return run.qaLoop?.aligned === false;
}

function blockingMessageFor(qaLoop: QaLoop | undefined, label: string) {
  return (qaLoop?.blockingMessages || []).filter((message) =>
    message.toLowerCase().includes(label.toLowerCase())
  );
}

/**
 * Derive the six gates from whatever the backend actually returned.
 *
 * Precedence, and there is no other:
 *   1. A backend level with this gate's id -> its verdict, and ONLY then may it be "passed".
 *   2. This gate's label appears in qaLoop.blockedLevels -> "blocked" (no verdict, but we
 *      know it is not clear).
 *   3. QA is running right now -> "running".
 *   4. Otherwise -> "not_run". The default. Always.
 *
 * Note what is absent: any reference to run.status. An approved run does not make a check
 * pass. In local-preview mode (apiUrl === "") approveRun() sets status "approved" with no QA
 * having executed at all — under the old code that painted six green ticks on six checks
 * that never ran.
 */
export function deriveGates(run: RunStatus, qaState: ActionState, discardResult = false): Gate[] {
  // A QA result computed against a DIFFERENT decision set is not a QA result. When any edit
  // is still undecided, no stored verdict can apply to what the operator is looking at, so
  // every gate resets to "not_run" rather than showing a stale pass or a stale blocker.
  const qaLoop = discardResult ? undefined : run.qaLoop;
  const levels = qaLoop?.levels || [];
  const blockedLabels = new Set((qaLoop?.blockedLevels || []).map((label) => label.toLowerCase()));

  // THE UNION, NOT A FIXED LIST. The six frozen ids in their frozen order come first; then any
  // requiredLevelId or returned level the backend added that we do not already list is APPENDED, so
  // a new gate can never be silently swallowed by iterating a hardcoded six. New validators map INTO
  // the six existing levels (the ids keep their exact spelling), but if the backend ever surfaces a
  // seventh required level, the operator sees it rather than the UI dropping it on the floor. The
  // displayed label follows the backend level when it provides one, else the frozen table, else the id.
  const ordered: { id: string; label: string }[] = GATE_DEFS.map((gate) => {
    const level = levels.find((item) => item.id === gate.id);
    return { id: gate.id, label: level?.label || gate.label };
  });
  const known = new Set(ordered.map((gate) => gate.id));
  for (const id of [...(qaLoop?.requiredLevelIds || []), ...levels.map((level) => level.id)]) {
    if (!id || known.has(id)) continue;
    known.add(id);
    ordered.push({ id, label: levels.find((level) => level.id === id)?.label || id });
  }

  return ordered.map<Gate>(({ id, label }) => {
    const level = levels.find((item) => item.id === id);

    if (level) {
      const verdict = String(level.status || "").toLowerCase();
      const messages = qaLevelMessages(level);
      // The ONLY place in the codebase where "passed" is produced, and it is produced from
      // a backend verdict for THIS gate id or not at all.
      if (verdict === "pass") return { id, label, state: "passed", evidence: level, messages };
      if (verdict === "fail") return { id, label, state: "failed", evidence: level, messages };
      if (verdict === "warn") {
        return {
          id,
          label,
          state: "warned",
          evidence: level,
          messages: messages.length
            ? messages
            : [`${label} raised a warning. It is reported for your attention and does not block export.`]
        };
      }
      // "missing", or any status word we do not recognise: the check did not produce a
      // verdict. That is NOT a pass, and it is not a failure either. It did not run.
      return { id, label, state: "not_run", evidence: null, messages: [] };
    }

    if (blockedLabels.has(label.toLowerCase())) {
      const messages = blockingMessageFor(qaLoop, label);
      return {
        id,
        label,
        state: "blocked",
        evidence: null,
        messages: messages.length
          ? messages
          : [`${label} did not clear. It needs a repair or a reviewer decision before export.`]
      };
    }

    if (qaState === "running") return { id, label, state: "running", evidence: null, messages: [] };

    return { id, label, state: "not_run", evidence: null, messages: [] };
  });
}

export type GateCounts = {
  passed: number;
  failed: number;
  running: number;
  notRun: number;
  /** Checks that produced a verdict of any kind. The numerator of "N of 6 checks run". */
  ran: number;
  total: number;
};

/**
 * Counts. NOT a percentage — this function CANNOT express one, which is the point.
 * There is no Math.max floor on a QA figure in this file, and there is none anywhere else in
 * the codebase — the remaining Math.max calls clamp measured elapsed seconds and an LCS table.
 */
export function gateCounts(gates: Gate[]): GateCounts {
  const passed = gates.filter((gate) => gate.state === "passed").length;
  const failed = gates.filter((gate) => gate.state === "failed" || gate.state === "blocked").length;
  const running = gates.filter((gate) => gate.state === "running").length;
  const notRun = gates.filter((gate) => gate.state === "not_run" || gate.state === "not_applicable").length;
  return { passed, failed, running, notRun, ran: passed + failed, total: gates.length };
}

/** Every blocking reason on the run, deduplicated, in gate order. */
export function gateBlockers(gates: Gate[]) {
  const seen = new Set<string>();
  const out: Array<{ gateId: string; gateLabel: string; message: string }> = [];
  for (const gate of gates) {
    if (gate.state !== "blocked" && gate.state !== "failed") continue;
    for (const message of gate.messages) {
      if (seen.has(message)) continue;
      seen.add(message);
      out.push({ gateId: gate.id, gateLabel: gate.label, message });
    }
  }
  return out;
}
